import { resolveHref } from 'next/dist/client/resolve-href'
import Router, { NextRouter } from 'next/router'
import { useEffect } from 'react'

// Each top-level Cuidei journey gets a fresh document. In particular GTM and
// Insider cannot be unloaded safely once they have executed on /farma.
export const crossesCuideiTagBoundary = (current: string, target: string): boolean => {
  const from = new URL(current)
  const to = new URL(target, from)
  if (from.origin !== to.origin) return false
  const family = (path: string) => {
    const parts = path.split('/').filter(Boolean)
    return parts[0] === 'patologia' ? parts[1] : parts[0]
  }
  return family(from.pathname) !== family(to.pathname)
}

export const installCuideiTagBoundary = (
  router: NextRouter,
  location: Pick<Location, 'href' | 'assign' | 'replace'>
): (() => void) => {
  // Popstate has already changed location.href when its callback runs. Keep
  // the current document's scope independently of the browser's target URL.
  const documentUrl = location.href
  const current = new URL(documentUrl)
  const originals = { push: router.push, replace: router.replace }
  const wrappers = {} as Pick<NextRouter, 'push' | 'replace'>
  for (const method of ['push', 'replace'] as const) {
    wrappers[method] = (url, as, options) => {
      const [href, resolved] = resolveHref(
        { ...router, pathname: current.pathname, asPath: current.pathname + current.search },
        as ?? url,
        true
      )
      const target = new URL(resolved ?? href, documentUrl)
      target.pathname = target.pathname.replace(/^\/patologia\/([a-z0-9]+(?:-[a-z0-9]+)*)(\/consulta)?\/?$/, '/$1$2')
      if (crossesCuideiTagBoundary(documentUrl, target.href)) {
        if (method === 'replace') location.replace(target.href)
        else location.assign(target.href)
        return Promise.resolve(false)
      }
      return originals[method].call(router, url, as, options)
    }
    router[method] = wrappers[method]
  }
  router.beforePopState(({ as }) => {
    if (!crossesCuideiTagBoundary(documentUrl, as)) return true
    location.replace(new URL(as, documentUrl).href)
    return false
  })
  return () => {
    for (const method of ['push', 'replace'] as const) {
      if (router[method] === wrappers[method]) router[method] = originals[method]
    }
    router.beforePopState(() => true)
  }
}

export const useCuideiTagBoundary = (router: NextRouter): void => {
  useEffect(() => {
    if (!window.location.hostname.toLowerCase().includes('cuidei')) return
    // NextLink, useRouter and the singleton facade delegate to this instance.
    return installCuideiTagBoundary(Router.router ?? router, window.location)
  }, [router])
}
